<script setup lang="ts">
  // The event menu: a full-height drawer from the left, opened from the hamburger in the header (see
  // app-header.vue). It replaces the bottom tab bar with its "More" sheet. The account menu (avatar,
  // top right) is components/page/account-menu.vue.
  import {VueFinalModal} from 'vue-final-modal';

  const route = useRoute();
  const router = useRouter();
  const wadUtils = useWadUtils();
  const {fixedWidthImageParams} = useImageCdn();
  const {hasPermission} = useWadAuth();
  const eventsStore = useEventsStore();
  const {eventData, eventHasEnded, communityAvailable} = storeToRefs(eventsStore);
  const {open, available, hasEventContext, hasEventSections, homeRoute, eventId, routeName} = useMainMenu();

  const ICONS = EVENT_SECTION_ICONS;
  // Attendees, partners, goodies and booth activities stay out of the menu until the agenda is live.
  const SECTIONS = computed(() => EVENT_SECTIONS.filter(section => communityAvailable.value || !section.community));

  // The filter query is what tells the two attendee entries apart.
  const onContacts = computed(() => route.query.filter === 'connected');

  const isActive = (names: string[], unlessContacts = false): boolean =>
    names.includes(routeName.value) && !(unlessContacts && onContacts.value);

  const customItems = computed(() => eventData.value?.menu_items ?? []);
  const isIframeItem = (item: {type: number}): boolean => item.type === wadConstants.EVENT_MENU_ITEM_TYPE.IFRAME;
  const isCustomActive = (item: {slug?: string}): boolean =>
    routeName.value === 'events-eventId-page-slug' && route.params.slug === item.slug;

  const onExternalItemClick = (url: string) => {
    if(!url) {
      return;
    }
    open.value = false;
    const urlInfo = wadUtils.getUrlInfo(url);
    if(urlInfo.isInternal) {
      router.push(urlInfo.internalPath);
    } else {
      window.open(url, '_blank');
    }
  };

  // Close when navigating, whichever way the navigation started.
  watch(() => route.fullPath, () => { open.value = false; });
  // Never stay open once the menu is gone (logout).
  watch(available, (isAvailable) => { if(!isAvailable) open.value = false; });

  const close = () => { open.value = false; };

  // "Plan with AI" lives in the agenda; the menu opens it from any page of the event.
  const aiPlanOpen = useState<boolean>('wad4-ai-plan-open', () => false);

  // The shimmer on the "Plan with AI" item is there to be noticed, so it stops once the user has opened Plan with AI
  // (from here, from the popup or from the empty My Agenda). The tile keeps its gradient, quietly. Kept in this browser.
  const AI_PLAN_TRIED_KEY = 'wad4-ai-plan-tried';
  const aiPlanTried = ref(false);
  onMounted(() => {
    try {
      aiPlanTried.value = localStorage.getItem(AI_PLAN_TRIED_KEY) === '1';
    } catch {
      // Storage blocked: the shimmer simply keeps going for this session.
    }
  });
  watch(aiPlanOpen, (isOpen) => {
    if(isOpen) {
      aiPlanTried.value = true;
      try {
        localStorage.setItem(AI_PLAN_TRIED_KEY, '1');
      } catch {
        // Nothing to do, see above.
      }
    }
  });
  const openAiPlan = async () => {
    close();
    if(routeName.value !== 'events-eventId-agenda') {
      await router.push({name: 'events-eventId-agenda', params: {eventId: eventId.value}});
    }
    aiPlanOpen.value = true;
  };
</script>

<template>
  <VueFinalModal v-if="available"
                 v-model="open"
                 teleport-to="body"
                 display-directive="if"
                 overlay-transition="vfm-fade"
                 content-transition="wad4-drawer-slide"
                 content-class="wad4-main-menu-vfm"
                 :click-to-close="true"
                 :esc-to-close="true"
                 :lock-scroll="true">
    <nav class="wad4-main-menu" aria-label="Main menu">
      <header class="wad4-main-menu__head">
        <div class="wad4-main-menu__top">
          <!-- The logo is the way home: it lives here now, not in the header. -->
          <nuxt-link :to="homeRoute" class="wad4-main-menu__logo" aria-label="WeAreDevelopers home" @click="close">
            <svg width="56" height="17" viewBox="0 0 56 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M30.3623 0.000103207H26.2731L21.7545 11.699L17.236 0.000103207H13.1468L8.60776 11.699L4.0892 0.000103207H0L6.56316 17H10.6524L15.1914 5.30115L19.7099 17H23.7991L28.3177 5.30115L32.8363 17H36.9459L30.3623 0.000103207Z" fill="var(--wad4-c-text-text-brand-wad)"/>
          <path d="M38.9889 0V4.04179L50.7045 8.48979L38.9889 12.9581V16.9999L56 10.5208V6.47905L38.9889 0Z" fill="var(--wad4-c-text-text-brand-wad)"/>
          </svg>
          </nuxt-link>
          <button type="button" class="wad4-main-menu__close" aria-label="Close menu" @click="close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </header>

      <div class="wad4-main-menu__body">
        <section v-if="hasEventSections" class="wad4-main-menu__group" aria-labelledby="wad4-main-menu-guide">
          <h2 id="wad4-main-menu-guide" class="wad4-main-menu__heading">
            <span class="wad4-main-menu__event">{{ eventData?.name }}</span>
          </h2>
          <ul class="wad4-main-menu__list">
            <li>
              <nuxt-link :to="{name: 'events-eventId', params: {eventId}}"
                         class="wad4-main-menu__item" v-track="{entity: 'menu', item: 'home', event: eventId}"
                         :class="{'wad4-main-menu__item--active': routeName === 'events-eventId'}"
                         :aria-current="routeName === 'events-eventId' ? 'page' : undefined"
                         @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.home" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Home
              </nuxt-link>
            </li>
            <li v-for="section in SECTIONS" :key="section.key">
              <nuxt-link :to="{name: section.route, params: {eventId}}"
                         class="wad4-main-menu__item" v-track="{entity: 'menu', item: section.key, event: eventId}"
                         :class="{'wad4-main-menu__item--active': isActive(section.active, section.unlessContacts)}"
                         :aria-current="isActive(section.active, section.unlessContacts) ? 'page' : undefined"
                         @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS[section.icon]" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ section.label }}
              </nuxt-link>
            </li>
            <li v-for="item in customItems" :key="`custom-${item.slug ?? item.url ?? item.label}`">
              <nuxt-link v-if="isIframeItem(item)"
                         :to="{name: 'events-eventId-page-slug', params: {eventId, slug: item.slug}}"
                         class="wad4-main-menu__item" v-track="{entity: 'menu', item: item.slug, event: eventId}"
                         :class="{'wad4-main-menu__item--active': isCustomActive(item)}"
                         @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.link" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ item.label }}
              </nuxt-link>
              <nuxt-link v-else :to="item.url" class="wad4-main-menu__item" v-track="{entity: 'menu', item: 'external-link', event: eventId}" @click.prevent="onExternalItemClick(item.url)">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.link" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ item.label }}
                <common-external-link-icon v-if="!wadUtils.getUrlInfo(item.url).isInternal"/>
              </nuxt-link>
            </li>
          </ul>
        </section>

        <section v-if="hasEventContext" class="wad4-main-menu__group" aria-labelledby="wad4-main-menu-mine">
          <h2 id="wad4-main-menu-mine" class="wad4-main-menu__heading">
            <span class="wad4-main-menu__eyebrow">My event</span>
          </h2>
          <ul class="wad4-main-menu__list">
            <li v-if="hasEventSections && !eventHasEnded && !eventData?.program_preview">
              <button type="button" class="wad4-main-menu__item wad4-main-menu__item--button wad4-main-menu__item--ai" v-track="{entity: 'menu', item: 'plan-ai', event: eventId}"
                      :class="{'wad4-main-menu__item--ai-new': !aiPlanTried}" @click="openAiPlan">
                <span class="wad4-main-menu__ai-tile" aria-hidden="true">
                  <!-- The agenda picture of the popup, small: three sessions and a star. -->
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <path d="M4 8h8M4 13h11M4 18h7" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round"/>
                    <path d="M18.5 3.5l1.1 3 3 1.1-3 1.1-1.1 3-1.1-3-3-1.1 3-1.1z" fill="#ffffff"/>
                  </svg>
                </span>
                <span class="wad4-main-menu__ai-label">Plan with AI</span>
              </button>
            </li>
            <li>
              <nuxt-link :to="{name: 'events-eventId-my-badge', params: {eventId}}" class="wad4-main-menu__item" v-track="{entity: 'menu', item: 'my-profile', event: eventId}"
                         :class="{'wad4-main-menu__item--active': routeName === 'events-eventId-my-badge'}" @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.profile" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                My Profile
              </nuxt-link>
            </li>
            <li v-if="hasPermission('app.scan-leads')">
              <nuxt-link :to="{name: 'events-eventId-leads', params: {eventId}}" class="wad4-main-menu__item"
                         :class="{'wad4-main-menu__item--active': ['events-eventId-leads', 'events-eventId-leads-id', 'events-eventId-leads-scan'].includes(routeName)}" @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.leads" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                My Leads
              </nuxt-link>
            </li>
            <li v-if="communityAvailable">
              <nuxt-link :to="{name: 'events-eventId-attendees', params: {eventId}, query: {filter: 'connected'}}" class="wad4-main-menu__item" v-track="{entity: 'menu', item: 'my-contacts', event: eventId}"
                         :class="{'wad4-main-menu__item--active': routeName === 'events-eventId-attendees' && onContacts}" @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.contacts" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                My Contacts
              </nuxt-link>
            </li>
          </ul>
        </section>

        <section class="wad4-main-menu__group wad4-main-menu__group--all-events" aria-label="Events">
          <ul class="wad4-main-menu__list">
            <li>
              <nuxt-link :to="{name: 'index'}" class="wad4-main-menu__item" v-track="{entity: 'menu', item: 'switch-event', event: eventId}"
                         :class="{'wad4-main-menu__item--active': routeName === 'index'}"
                         :aria-current="routeName === 'index' ? 'page' : undefined"
                         @click="close">
                <svg class="wad4-main-menu__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path :d="ICONS.switchEvent" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ SWITCH_EVENT_LABEL }}
              </nuxt-link>
            </li>
          </ul>
        </section>
      </div>
    </nav>
  </VueFinalModal>
</template>

<style lang="scss">
// VFM content element: full-screen flex container that left-anchors the drawer, so the slide
// transform has room to move it. It must not swallow taps on the dimmed area beside the drawer,
// those fall through to the vfm root so click-to-close fires.
.wad4-main-menu-vfm {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: flex-start;
  pointer-events: none;
}

.wad4-main-menu {
  display: flex;
  flex-direction: column;
  width: min(86vw, 340px);
  height: 100%;
  pointer-events: auto;
  background: $wad4-c-background-bg-primary;
  color: $wad4-c-text-text-primary;
  border-right: 1px solid $wad4-c-border-border-subtle;
  // Below the iOS notch and above the home indicator in standalone PWA mode.
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);

  &__head {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-lg;
    padding: $wad4-spacing-lg $wad4-container-padding;
    border-bottom: 1px solid $wad4-c-border-border-subtle;
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $wad4-spacing-lg;
  }

  &__logo {
    display: inline-flex;
  }

  &__close {
    flex-shrink: 0;
    display: inline-flex;
    // 10px + 24px icon + 10px = a 44px tap target.
    padding: 10px;
    border: none;
    border-radius: $wad4-border-radius-md;
    background: none;
    color: $wad4-c-foreground-fg-primary;
    cursor: pointer;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }
  }

  // The head stays put; only the sections scroll, so the logo and close button never leave.
  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
  }

  &__group {
    padding: $wad4-spacing-md 0;

    & + & {
      border-top: 1px solid $wad4-c-border-border-subtle;
    }

    // Sits at the bottom when the sections above are short. On pages without any sections it is the
    // first block and starts at the top instead of floating in empty space.
    &--all-events {
      padding-bottom: $wad4-spacing-xl;
    }

    &--all-events:not(:first-child) {
      margin-top: auto;
    }
  }

  &__heading {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: $wad4-spacing-md $wad4-container-padding $wad4-spacing-sm;
  }

  &__eyebrow {
    color: $wad4-c-text-text-tertiery;
    @include text-size-mixin('sm');
    font-weight: $wad4-font-weight-semi-bold;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__event {
    @include text-size-mixin('md');
    font-weight: $wad4-font-weight-semi-bold;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: $wad4-spacing-xl;
    padding: $wad4-spacing-lg $wad4-container-padding;
    color: $wad4-c-text-text-primary;
    @include text-size-mixin('lg');
    font-weight: $wad4-font-weight-regular;
    text-decoration: none;

    @include hover {
      background: $wad4-c-background-bg-secondary;
    }

    &--active {
      color: $wad4-c-text-text-brand-tertiery;
      background: $wad4-c-background-bg-brand-primary;
      // A bar on the leading edge, so the current section reads without relying on colour alone.
      box-shadow: inset 3px 0 0 $wad4-c-background-bg-brand-solid;
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  // The AI item: the icon on a small gradient tile (the brand blue, a violet and the WAD red) and a label that
  // shimmers with the same colours, slowly, until the user has opened Plan with AI once. Still when the user prefers
  // reduced motion.
  &__item--ai {
    --ai-1: #3746f4;
    --ai-2: #8a3ffc;
    --ai-3: #ff2f6b;
  }

  &__ai-tile {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    // Same footprint as the 24px icons of the other rows, so the labels stay in line.
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: linear-gradient(135deg, var(--ai-1), var(--ai-2) 55%, var(--ai-3));
    background-size: 160% 160%;
  }

  &__ai-label {
    background: linear-gradient(90deg, var(--ai-1), var(--ai-2), var(--ai-3), var(--ai-2), var(--ai-1));
    background-size: 220% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    font-weight: $wad4-font-weight-semi-bold;
  }

  &__item--ai-new &__ai-label {
    animation: wad4-main-menu-shimmer 7s linear infinite;
  }

  &__item--ai-new &__ai-tile {
    animation: wad4-main-menu-tile 7s ease-in-out infinite alternate;
  }

  // A menu row that is a button (it opens a sheet instead of going to a page): same look as the links.
  &__item--button {
    width: 100%;
    border: none;
    background: none;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }
}

:root[data-theme='dark'] .wad4-main-menu__item--ai {
  --ai-1: #8fa0ff;
  --ai-2: #c79bff;
  --ai-3: #ff8fb0;
}

@keyframes wad4-main-menu-shimmer {
  from { background-position: 0% 50%; }
  to   { background-position: 220% 50%; }
}

@keyframes wad4-main-menu-tile {
  from { background-position: 0% 30%; }
  to   { background-position: 100% 70%; }
}

@media (prefers-reduced-motion: reduce) {
  .wad4-main-menu__item--ai-new .wad4-main-menu__ai-label,
  .wad4-main-menu__item--ai-new .wad4-main-menu__ai-tile {
    animation: none;
  }
}

.wad4-drawer-slide-enter-active,
.wad4-drawer-slide-leave-active {
  transition: transform 0.25s ease;
}
.wad4-drawer-slide-enter-from,
.wad4-drawer-slide-leave-to {
  transform: translateX(-100%);
}
</style>
